/* eslint-disable import/no-unresolved */
import React, { Component, Suspense } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { matchPath, withRouter } from "react-router";
import queryString from "query-string";
import { NavLink, Switch, Route, Redirect } from "react-router-dom";

import audienceSlice from "../../../store/entities/audience/audience-slice.reducer";
import { ModalShare } from "../../../components/complex/ModalShare/ModalShare";
import {
  Container,
  Row,
  ModalView,
  Button,
  LoadingIndicator
} from "../../../components/simple";
import Header from "./Header";

import {
  fullReport,
  audiencesCreateWithoutTemplate,
  trash
} from "../../../router/routes";

import Api from "../../../api";

import LimitWarning from "../PushModal/LimitWarning";
import PushModal from "../PushModal";
import { ModalSelectClientBeforePush } from "../../../components/forms/ModalSelectClientBeforePush";
import {
  clearSamMapData,
  clearStats
} from "../../../store/entities/audience/audience.actions";

import { permissions, AccessControl } from "../../../components/permissions";
import PrivateRoute from "../../../router/PrivateRoute";

import styles from "./full-report.module.scss";

const Audience = React.lazy(() => import("./Audience"));
const Map = React.lazy(() => import("./Map"));
const Demographics = React.lazy(() => import("./Demographics"));
const SAM = React.lazy(() => import("./Chart"));

const {
  actions: { showShareAudienceModal: showShareAudienceModalAction }
} = audienceSlice;

class ReportsScreen extends Component {
  constructor(props) {
    super(props);

    const {
      location: { state }
    } = props;

    this.state = {
      audience: {},
      ...state,
      adformToken: "",
      snapchatToken: "",
      isShowPushModal: false,
      isLimitWarningModalOpen: false,
      isSelectClientModalOpen: false,
      isAudienceUpdated: false,
      selectedClient: null,
      isShowEditPresetModal: false,
      confirmModalHeader: "",
      confirmModalLabel: "",
      confirmButtonLabel: "",
      confirmAction: () => {}
    };

    this.handleGoBack = this.handleGoBack.bind(this);
    this.handleEditPreset = this.handleEditPreset.bind(this);
    this.handleShare = this.handleShare.bind(this);
  }

  componentDidMount() {
    document.title = "Audience | Full report";

    this.props.clearStats();

    const params = queryString.parse(this.props.location.search);

    const matched = matchPath(this.props.location.pathname, {
      path: `${fullReport}/:id/:threshold/:type`,
      exact: false,
      strict: false
    });

    const { id, threshold, type } = matched.params;

    const data =
      type === "audience"
        ? { audienceId: id, threshold, type }
        : { presetId: id, threshold, type };

    Api.audience.map({
      data
    });

    if (params.adformToken) {
      this.setState({
        adformToken: params.adformToken,
        selectedClient: this.props.location.state.clientId,
        isShowPushModal: true
      });
    }

    if (params.snapchatToken) {
      this.setState({
        snapchatToken: params.snapchatToken,
        selectedClient: this.props.location.state.clientId,
        isShowPushModal: true
      });
    }

    if (!Object.keys(this.props.clients).length) {
      Api.client.get();
    }

    this.getSubscription();

    this.getStats();

    if (!this.props.user.company) this.getUserProfile();
  }

  async componentDidUpdate(prevProps, prevState) {
    if (
      this.state.isAudienceUpdated !== prevState.isAudienceUpdated &&
      this.state.isAudienceUpdated === true
    ) {
      this.showModal();

      await this.getStats().then(() => {
        this.setState({ isAudienceUpdated: false });
      });
    }

    if (
      this.props.isDublicateSuccess !== prevProps.isDublicateSuccess &&
      this.props.isDublicateSuccess === true
    ) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ isShowEditPresetModal: false });
      // TODO: Open audience builder
    }
  }

  showModal = () => {
    if (!this.props.companyPlan.limitedFunctionality)
      this.setState({ isShowPushModal: true });
    else if (this.props.companyPlan.limitedFunctionality)
      this.setState({
        isLimitWarningModalOpen: true
      });
  };

  setIsShowPushModal = state => this.setState({ isShowPushModal: state });

  setIsLimitWarningModalOpen = state =>
    this.setState({ isLimitWarningModalOpen: state });

  getSamMapData = async () => {
    const { location } = this.props;
    const matched = matchPath(location.pathname, {
      path: `${fullReport}/:id/:threshold/:type`,
      exact: false,
      strict: false
    });
    const { id } = matched.params;

    await Api.audience.samMap({ params: { audience_id: id } });
  };

  getSubscription = async () => {
    await Api.subscription.get();
  };

  getStats = async () => {
    const { location } = this.props;
    const matched = matchPath(location.pathname, {
      path: `${fullReport}/:id/:threshold/:type`,
      exact: false,
      strict: false
    });

    const { id, threshold, type } = matched.params;

    const data =
      type === "audience"
        ? { audienceId: id, threshold }
        : { presetId: id, threshold };

    const stats = await Api.audience.stats({
      data: { ...data, type }
    });

    if (
      stats.audienceVariables?.length === 1 &&
      stats.audienceVariables?.[0]?.length === 1 &&
      stats.audienceVariables?.[0]?.[0].custom
    ) {
      this.getSamMapData();
    } else {
      this.props.clearSamMapData();
    }

    this.setState(prevState => ({ ...prevState, audience: stats }));
  };

  getUserProfile = async () => {
    await Api.user.getUserInfo();
  };

  handleSelectClientModal = isSelectClientModalOpen => {
    return this.setState({ isSelectClientModalOpen });
  };

  onSelectClient = async data => {
    const { id, name, clientsId } = data;
    const { location } = this.props;

    const matched = matchPath(location.pathname, {
      path: `${fullReport}/:id/:threshold/:type`,
      exact: false,
      strict: false
    });

    const { type } = matched.params;

    if (type === "audience") {
      const response = await Api.audience.edit({
        id,
        data: {
          name,
          client: clientsId
        }
      });

      if (response.id)
        this.setState({ isAudienceUpdated: true, selectedClient: clientsId });
    } else if (type === "preset") {
      this.setState({ selectedClient: clientsId, isShowPushModal: true });
    }

    this.handleSelectClientModal(false);
  };

  handleShare = () => {
    this.props.showShareAudienceModal(this.state.audience.id);
  };

  async handleGoBack() {
    const {
      history,
      location: { pathname },
      stats
    } = this.props;

    // const data = Object.keys(stats).length ? stats : state;
    const data = this.state.audience;

    const matched = matchPath(pathname, {
      path: `${fullReport}/:id/:threshold/:type`,
      exact: false,
      strict: false
    });

    const audienceId = matched.params.id;

    if (stats?.active) {
      history.push(audiencesCreateWithoutTemplate(audienceId), {
        selectedVariables: data.audienceVariables,
        country: data.country.name,
        audience: data.threshold
      });
    } else {
      history.push(trash);
    }
  }

  handleEditPreset() {
    const { id } = this.state.audience;
    const { history } = this.props;
    this.setState({
      isShowEditPresetModal: true,
      confirmModalHeader: "Edit preset",
      confirmModalLabel:
        "You need to create audience from preset to be able to edit it.",
      confirmAction: () => {
        Api.audience.duplicateAudience({
          // don't use location.state cause of case when user opens copied link
          data: {
            audienceId: id,
            isPreset: !!history.location.pathname.match("preset")
          },
          redirectTo: history.push
        });
      },
      confirmButtonLabel: "Create"
    });
  }

  render() {
    const { location, samMapData, user, isDubliciting } = this.props;
    const {
      isShowPushModal,
      isLimitWarningModalOpen,
      adformToken,
      snapchatToken,
      selectedClient,
      audience
    } = this.state;

    const matched = matchPath(location.pathname, {
      path: `${fullReport}/:id/:threshold/:type`,
      exact: false,
      strict: false
    });

    const { id, threshold, type } = matched.params;

    return (
      <Container className={styles["precampaign-report"]} fluid>
        <Row>
          <Header
            params={matched.params}
            onGoBack={this.handleGoBack}
            // setIsSelectClientModalOpen={this.handleSelectClientModal}
            setIsSelectClientModalOpen={() =>
              this.setState({ isSelectClientModalOpen: true })
            }
            isAudienceActive={audience?.active}
          />
        </Row>
        <Row className={styles.tabs}>
          <NavLink
            className={styles.tab}
            activeClassName={styles.active}
            to={`${fullReport}/${id}/${threshold}/${type}`}
            exact
          >
            {type === "audience" ? "Audience" : "Preset"}
          </NavLink>
          <NavLink
            className={styles.tab}
            activeClassName={styles.active}
            to={`${fullReport}/${id}/${threshold}/${type}/map`}
          >
            Map
          </NavLink>
          <AccessControl allow={permissions.advancedReporting.view}>
            <NavLink
              className={styles.tab}
              activeClassName={styles.active}
              to={`${fullReport}/${id}/${threshold}/${type}/demographics`}
            >
              Key Demographics
            </NavLink>
          </AccessControl>

          {Object.keys(samMapData).length ? (
            <NavLink
              className={styles.tab}
              activeClassName={styles.active}
              to={`${fullReport}/${id}/${threshold}/${type}/sam`}
            >
              SAM Map
            </NavLink>
          ) : null}

          {((type === "audience" && audience?.active) || type === "preset") && (
            <button
              type="button"
              className={styles.tab}
              onClick={
                type === "preset" ? this.handleEditPreset : this.handleGoBack
              }
            >
              Edit
            </button>
          )}

          {audience?.active ? (
            <button
              type="button"
              className={styles.tab}
              onClick={this.handleShare}
            >
              Share
            </button>
          ) : null}
        </Row>
        <Row>
          <Suspense fallback={<div>Loading...</div>}>
            <Switch>
              <Route
                path={`${fullReport}/:id/:threshold/:type`}
                exact
                render={routeProps => (
                  // <Audience {...routeProps} data={this.state} />
                  <Audience {...routeProps} data={this.state} />
                )}
              />

              <Route
                path={`${fullReport}/:id/:threshold/:type/map`}
                component={Map}
              />

              <PrivateRoute
                exact
                path={`${fullReport}/:id/:threshold/:type/demographics`}
                component={Demographics}
                allow={permissions.advancedReporting.view}
                redirectTo={`${fullReport}/${id}/${threshold}/${type}`}
                withOutSidebar
              />

              <PrivateRoute
                exact
                path={`${fullReport}/:id/:threshold/:type/sam`}
                component={SAM}
                allow={permissions.preCampaignReport.view}
                redirectTo={`${fullReport}/${id}/${threshold}/${type}`}
                withOutSidebar
              />

              <Redirect to={`${fullReport}/${id}/${threshold}/${type}`} />
            </Switch>
          </Suspense>
        </Row>
        <PushModal
          isOpen={isShowPushModal}
          onClose={() => this.setState({ isShowPushModal: false })}
          // audience={Object.keys(this.state).length ? this.state : stats}
          audience={this.state.audience}
          adformToken={adformToken}
          snapchatToken={snapchatToken}
          user={user}
          clientId={selectedClient}
          currentSubscribtion={user.company.currentSubscribtion}
        />
        {isLimitWarningModalOpen && (
          <ModalView
            isOpen={isLimitWarningModalOpen}
            handleClose={() =>
              this.setState({ isLimitWarningModalOpen: false })
            }
          >
            <LimitWarning
              onShow
              onPushAnyway={() => {
                this.setState({
                  isLimitWarningModalOpen: false,
                  isShowPushModal: true
                });
              }}
            />
          </ModalView>
        )}
        <ModalSelectClientBeforePush
          modalTitle="Select your client"
          data={
            Object.keys(this.props.stats).length
              ? this.props.stats
              : this.props.location.state?.audience
          }
          type={type}
          clients={this.props.clients}
          isOpen={this.state.isSelectClientModalOpen}
          changeModalState={() =>
            this.handleSelectClientModal(!this.state.isSelectClientModalOpen)
          }
          handleSubmit={this.onSelectClient}
        />
        <ModalShare />
        {/* <ConfirmModal
          message={`{title: "test", description: "description" }`}
          callback={() => {
            console.log("confitm");
          }}
        /> */}

        <ModalView
          isOpen={this.state.isShowEditPresetModal}
          toggle={() => {
            this.setState({ isShowEditPresetModal: false });
          }}
          handleClose={() => {
            this.setState({ isShowEditPresetModal: false });
          }}
        >
          <h2 className="title text-center mb-3">
            {this.state.confirmModalHeader}
          </h2>
          <p className="description mb-4" style={{ textAlign: "center" }}>
            {this.state.confirmModalLabel}
          </p>
          <div className="buttons-container">
            <Button
              onClick={() => {
                this.state.confirmAction();
              }}
              className="button button-danger justify-content-center ml-auto mr-auto mt-4"
            >
              {this.state.confirmButtonLabel}
            </Button>
          </div>
          {isDubliciting && (
            <div className={styles["loader-container"]}>
              <LoadingIndicator />
            </div>
          )}
        </ModalView>
      </Container>
    );
  }
}
const mapStateToProps = ({
  audiences,
  audienceSlice: audienceSliceState,
  userProfile,
  clients
}) => {
  return {
    stats: audiences.stats,
    clients: clients.data,
    samMapData: audiences.samMap,
    user: userProfile.user,
    companyPlan: userProfile.companyPlan,
    mapLayouts: audiences.map,
    isDublicateSuccess: audienceSliceState.isDublicateSuccess,
    isDubliciting: audienceSliceState.isDubliciting
  };
};

const mapDispatchToProps = dispatch => ({
  clearStats: () => dispatch(clearStats()),
  clearSamMapData: () => dispatch(clearSamMapData()),
  showShareAudienceModal: id => dispatch(showShareAudienceModalAction(id))
});

ReportsScreen.propTypes = {
  audience: PropTypes.shape({}).isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string,
    state: PropTypes.shape({
      audience: PropTypes.string,
      id: PropTypes.string,
      clientId: PropTypes.string
    }),
    search: PropTypes.string
  }).isRequired,
  history: PropTypes.shape({
    push: PropTypes.func,
    replace: PropTypes.func,
    location: PropTypes.shape({
      pathname: PropTypes.string
    })
  }).isRequired,
  stats: PropTypes.shape({
    active: PropTypes.string,
    audienceVariables: PropTypes.arrayOf(
      PropTypes.arrayOf(PropTypes.shape({}))
    ),
    country: PropTypes.shape({ name: PropTypes.string })
  }).isRequired,
  user: PropTypes.shape({
    company: PropTypes.shape({
      currentSubscribtion: PropTypes.string
    })
  }).isRequired,
  companyPlan: PropTypes.shape({
    limitedFunctionality: PropTypes.bool
  }).isRequired,
  samMapData: PropTypes.shape({}).isRequired,
  clearStats: PropTypes.func.isRequired,
  clearSamMapData: PropTypes.func.isRequired,
  showShareAudienceModal: PropTypes.func.isRequired,
  clients: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  mapLayouts: PropTypes.shape({}).isRequired,
  isDublicateSuccess: PropTypes.bool.isRequired,
  isDubliciting: PropTypes.bool.isRequired
};

const ReportsScreenWithRouter = withRouter(ReportsScreen);

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ReportsScreenWithRouter);
